import React from "react";
import { Link } from "react-router-dom";
import usePageMeta from "@/lib/usePageMeta";
import { BRAND } from "@/lib/api";
import { ArrowLeft } from "@phosphor-icons/react";

/**
 * Shared legal-page shell — keeps Privacy, Terms & Data-Deletion visually
 * consistent and on-brand with the rest of the site.
 */
export default function LegalLayout({ title, subtitle, lastUpdated, children, path }) {
  usePageMeta({ title, path, description: subtitle });
  return (
    <div data-testid="legal-page">
      <section data-nav-theme="dark" className="bg-navy-deep text-white py-20 min-h-[40vh] flex items-center px-5 md:px-10 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-30 pointer-events-none"/>
        <div className="relative max-w-3xl mx-auto w-full">
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-white/70 hover:text-white mb-5"><ArrowLeft size={12}/>Back to site</Link>
          <div className="eyebrow !text-white/55">Legal</div>
          <h1 className="font-display display-lg tracking-wide mt-3 leading-tight">{title}</h1>
          {subtitle && <p className="mt-4 text-slate-300 max-w-2xl text-sm md:text-base">{subtitle}</p>}
          {lastUpdated && <p className="mt-3 text-[11px] uppercase tracking-[0.22em] text-white/40">Last updated · {lastUpdated}</p>}
        </div>
      </section>

      <section data-nav-theme="light" className="max-w-3xl mx-auto px-5 md:px-8 py-16 w-full">
        <article className="prose prose-lg prose-slate max-w-none
          prose-headings:font-display prose-headings:tracking-wide prose-headings:text-navy
          prose-h2:text-2xl prose-h2:mt-14 prose-h2:mb-5 prose-h2:pb-3 prose-h2:border-b prose-h2:border-slate-200
          prose-h3:text-lg prose-h3:mt-10 prose-h3:mb-3
          prose-p:text-slate-700 prose-p:leading-[1.85] prose-p:my-5
          prose-li:text-slate-700 prose-li:leading-[1.8] prose-li:my-2
          prose-ul:my-6 prose-ul:space-y-2
          prose-a:text-navy prose-a:no-underline hover:prose-a:underline prose-a:font-medium
          prose-strong:text-navy prose-strong:font-semibold
          prose-hr:my-12 prose-hr:border-slate-200
          first:prose-p:mt-0">
          {children}
        </article>

        <div className="mt-14 pt-8 border-t border-slate-200 text-sm text-slate-500">
          Questions? Reach us at <a href={`mailto:${BRAND.email}`} className="text-navy underline">{BRAND.email}</a> or {BRAND.phone}.
        </div>
      </section>
    </div>
  );
}
